import { Control, Controller } from 'react-hook-form';

import { RoomsSelect } from 'components/Pages/Admin/Itinerary/Details/RoomsSelect';
import { Alert } from 'components/shared/Alert';
import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import { TSelectChangeHandler } from 'components/shared/BaseFormComponents/Select/types';
import Divider from 'components/shared/Divider';
import { FormAccordion, FormAccordionItem } from 'components/shared/FormAccordion';
import { HookCheckbox, HookInput, HookTimeInput } from 'components/shared/HookFormComponents';
import { HookWysiwyg } from 'components/shared/HookFormComponents/Wysiwyg';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Text from 'components/shared/Typography/Text';
import { PAGE_TYPE_PROPERTY, PAGE_TYPE_MISC } from 'constants/index';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { getOptionsLoader } from '../../functions';
import cs from '../../index.module.css';
import IconPicker from '../../shared/IconPicker';
import { DayOrDatePicker } from './DayOrDatePicker';
import * as functions from './functions';
import { useAccomodationFormContext, useDefaultValues, useRooms } from './hooks';
import s from './index.module.css';
import { AccomodationFormProps } from './types';

export const AccommodationForm = function ({ onCreateOption }: AccomodationFormProps) {
  useDefaultValues();

  const {
    methods: { control, watch, getValues, setValue },

    isVisibleFormBody,
    isAccomodationAlertVisible,

    isDayRoom,
    nightsCount,
  } = useAccomodationFormContext();

  const tripEventType = watch('type');
  const propertyPage = watch('attachedPage');

  const { rooms, handleRoomCreation, basisOfStay, loadBasisOfStayOptions } = useRooms();

  // TODO Add RQ Select options
  const loadPropertiesOptions = getOptionsLoader([PAGE_TYPE_PROPERTY, PAGE_TYPE_MISC], createOptionFromPageV2);

  // TODO: rework control props for adapters & delete it
  const depricatedControl = control as Control<any>;

  const handleOpenRoomPreview = (roomId: number) => functions.openRoomPreview(roomId, { rooms, propertyPage });

  const handleAttachedPage: TSelectChangeHandler = (name, id, config) => {
    const attachedPage = functions.getNewAttachedPage(name, id, config) as any;
    const displaySettings = functions.getNewDisplaySettings(name);

    setValue('attachedPage', attachedPage, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue(
      'data',
      {
        ...getValues(),
        rooming: [],
        notes: null,
        isAlternate: false,
        isCollapsible: false,
        checkIn: '',
        checkOut: '',
        confirmationNumber: '',
        contactInfo: '',
        website: '',
        location: '',
        roomBasis: null,
        displaySettings,
      },
      { shouldDirty: true },
    );
    setValue('data.rooming', [], { shouldDirty: true, shouldValidate: true });
  };

  return (
    <form className={cs.form}>
      <FormAccordion>
        <FormAccordionItem header="Required Information" isRequired>
          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Content Page
              </Text>
              <Text color="gray-600" size="medium">
                Search Property and Misc pages
                <br />
                (Misc pages won’t show on the map)
              </Text>
            </div>

            <div>
              <Controller
                name="attachedPage"
                control={control}
                render={({ field: { value }, fieldState: { error } }) => {
                  return (
                    <div>
                      <BaseSelect
                        placeholder="Search Content Library"
                        isClearable
                        defaultOption={value}
                        loadOptions={loadPropertiesOptions}
                        optionActionIcon={Visibility}
                        onOptionActionClick={functions.openPagePreview}
                        onChange={handleAttachedPage}
                        errorMessage={error?.message}
                        isCreatable
                        onOptionCreation={onCreateOption}
                      />

                      {isVisibleFormBody && isAccomodationAlertVisible && (
                        <div>
                          <Alert
                            className={s.warning}
                            variant="warning"
                            content="Heads up! This accommodation will not show on the map. Only Property Pages are included on the map."
                          />
                        </div>
                      )}

                      <div className={s.checkboxContainer}>
                        <HookCheckbox name="data.isAlternate" control={depricatedControl} className={cs.checkboxText}>
                          <Text color="gray-600" size="medium">
                            Alternative Accommodation
                          </Text>
                        </HookCheckbox>

                        <HookCheckbox name="data.isCollapsible" control={depricatedControl} className={cs.checkboxText}>
                          <Text color="gray-600" size="medium">
                            Collapse content on Day page
                          </Text>
                        </HookCheckbox>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </div>

          <Divider />

          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Dates of Stay
              </Text>
              <Text color="gray-600" size="medium">
                For a Day Room, leave the single day. <br />
                For an Overnight Stay click to edit.
              </Text>
            </div>
            <div>
              <DayOrDatePicker />
            </div>
          </div>

          <Divider />

          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Icons
              </Text>
              <Text color="gray-600" size="medium">
                Optionally customize your icon,
                <br />
                because we’re awesome.
              </Text>
            </div>
            <div>
              <IconPicker
                name="icon"
                control={depricatedControl}
                eventType={tripEventType}
                isImprovedUIOnEventModalsFeature
              />
            </div>
          </div>
        </FormAccordionItem>

        {isVisibleFormBody && (
          <>
            <FormAccordionItem header="Details">
              <div className={s.inputGroup}>
                <div className={s.roomTypeContainer}>
                  <Controller
                    name="data.rooming"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => {
                      return (
                        <RoomsSelect
                          defaultOption={value}
                          placeholder="Room Type"
                          options={rooms}
                          onOptionsChange={() => null}
                          optionActionIcon={Visibility}
                          onOptionActionClick={handleOpenRoomPreview}
                          onOptionCreation={handleRoomCreation}
                          onChange={onChange}
                          errorMessage={error?.message}
                        />
                      );
                    }}
                    defaultValue={[]}
                  />

                  <Controller
                    name="data.roomBasis"
                    control={control}
                    render={({ field: { onChange }, fieldState: { error } }) => {
                      return (
                        <div key={basisOfStay?.id}>
                          <BaseSelect
                            placeholder="Basis Of Stay"
                            isClearable
                            defaultOption={basisOfStay}
                            loadOptions={loadBasisOfStayOptions}
                            onOptionCreation={() => {}}
                            onChange={onChange}
                            errorMessage={error?.message}
                            isCreatable
                          />
                        </div>
                      );
                    }}
                  />
                </div>

                <div className={s.timeContainer}>
                  <HookTimeInput name="data.checkIn" control={depricatedControl} label="Arrival Time" />
                  <HookTimeInput name="data.checkOut" control={depricatedControl} label="Departure Time" />
                </div>

                <HookInput name="data.confirmationNumber" control={depricatedControl} label="Confirmation Number" />
              </div>
            </FormAccordionItem>

            <FormAccordionItem header="Contacts">
              <div className={s.inputGroup}>
                <div className={s.timeContainer}>
                  <HookInput name="data.contactInfo" control={depricatedControl} label="Contact Information" />
                  <HookInput name="data.website" control={depricatedControl} label="Website" />
                </div>
                <HookInput name="data.location" control={depricatedControl} label="Location or Address" />
              </div>
            </FormAccordionItem>

            <FormAccordionItem header="Notes">
              <HookWysiwyg name="data.notes" control={depricatedControl} placeholder="Anything else..." />
            </FormAccordionItem>

            <FormAccordionItem header="Event Title">
              <div className={s.detailsContainer}>
                <HookInput
                  name="data.displaySettings.checkIn"
                  control={depricatedControl}
                  label="Check in Day"
                  isDisabled={!propertyPage}
                />

                <HookInput
                  name="data.displaySettings.overnight"
                  control={depricatedControl}
                  label="Overnight Day(s)"
                  isDisabled={!propertyPage || isDayRoom || nightsCount === 1}
                />

                <HookInput
                  name="data.displaySettings.checkOut"
                  control={depricatedControl}
                  label="Check out Day"
                  isDisabled={!propertyPage || isDayRoom}
                />
              </div>
            </FormAccordionItem>

            <div className={cs.marginBottom} />
          </>
        )}
      </FormAccordion>
    </form>
  );
};
